<script setup>
import { computed, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useEChart } from '@/composables/useEChart'
import { useViewport } from '@/composables/useViewport'
import { stockDailyApi } from '@/api/stock'

const route = useRoute()
const router = useRouter()
const { isMobile } = useViewport()

const symbol = ref(route.params.symbol || '')
const stockName = ref(route.query.name || '')
const lookbackDays = ref(Number(route.query.lookback || 80))
const boundaryWindow = ref(Number(route.query.window || 9))

const { chartRef, init, resize } = useEChart()
const loading = ref(false)
const dataReady = ref(false)
const latestQuote = ref(null)
const channelInfo = ref(null)

const statusText = computed(() => {
  const info = channelInfo.value
  if (!info || info.status === 'none') return '无明显边界'
  if (info.status === 'breakout') return '上轨突破'
  if (info.status === 'inside') return '通道内'
  if (info.status === 'below') return '跌破下轨'
  return '识别中'
})

const statusClass = computed(() => {
  const status = channelInfo.value?.status
  if (status === 'breakout') return 'up'
  if (status === 'inside' || status === 'below') return 'down'
  return ''
})

function goBack () {
  router.back()
}

function toNum (value) {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function formatNumber (val, digits = 2) {
  const num = toNum(val)
  if (num === null) return '-'
  return num.toFixed(digits)
}

function formatPercent (val) {
  const num = toNum(val)
  if (num === null) return '-'
  return `${num > 0 ? '+' : ''}${num.toFixed(2)}%`
}

function calcRollingEnvelope (list, startIndex, windowSize) {
  const upperRaw = []
  const lowerRaw = []

  for (let i = 0; i < list.length; i++) {
    if (i < startIndex) {
      upperRaw.push(null)
      lowerRaw.push(null)
      continue
    }

    const from = Math.max(startIndex, i - windowSize + 1)
    const to = i
    const highs = []
    const lows = []
    for (let j = from; j <= to; j++) {
      const high = toNum(list[j].high)
      const low = toNum(list[j].low)
      if (high !== null) highs.push(high)
      if (low !== null) lows.push(low)
    }

    upperRaw.push(highs.length ? Math.max(...highs) : null)
    lowerRaw.push(lows.length ? Math.min(...lows) : null)
  }

  return {
    upperRaw,
    lowerRaw
  }
}

function smoothEnvelope (list, rawSeries, startIndex, windowSize, type) {
  const result = []

  for (let i = 0; i < list.length; i++) {
    if (i < startIndex || rawSeries[i] === null) {
      result.push(null)
      continue
    }

    const from = Math.max(startIndex, i - windowSize + 1)
    const to = i
    const values = []
    for (let j = from; j <= to; j++) {
      if (rawSeries[j] !== null) values.push(rawSeries[j])
    }
    if (!values.length) {
      result.push(null)
      continue
    }

    const avg = values.reduce((sum, value) => sum + value, 0) / values.length
    const high = toNum(list[i].high)
    const low = toNum(list[i].low)
    const bounded = type === 'upper'
      ? Math.max(avg, high ?? avg)
      : Math.min(avg, low ?? avg)
    result.push(Number(bounded.toFixed(2)))
  }

  return result
}

function calcSlope (series, span = 12) {
  const valid = series
    .map((value, index) => ({ value, index }))
    .filter(item => item.value !== null)
  if (valid.length < 2) return null

  const tail = valid.slice(-span)
  const first = tail[0]
  const last = tail[tail.length - 1]
  if (last.index === first.index) return null
  return (last.value - first.value) / (last.index - first.index)
}

function calcInsideRatio (list, upperSeries, lowerSeries, startIndex) {
  let total = 0
  let inside = 0
  for (let i = startIndex; i < list.length; i++) {
    const high = toNum(list[i].high)
    const low = toNum(list[i].low)
    const upper = upperSeries[i]
    const lower = lowerSeries[i]
    if (high === null || low === null || upper === null || lower === null) continue
    total += 1
    if (high <= upper && low >= lower) inside += 1
  }
  return total ? inside / total : 0
}

function calcChannel (list) {
  const lookback = Math.max(30, Math.min(160, lookbackDays.value || 80))
  const windowSize = Math.max(5, Math.min(31, boundaryWindow.value || 9))
  const startIndex = Math.max(0, list.length - lookback)
  const envelope = calcRollingEnvelope(list, startIndex, windowSize)
  const upperSeries = smoothEnvelope(list, envelope.upperRaw, startIndex, windowSize, 'upper')
  const lowerSeries = smoothEnvelope(list, envelope.lowerRaw, startIndex, windowSize, 'lower')

  const lastIndex = list.length - 1
  const last = list[lastIndex]
  const close = toNum(last?.close)
  const upperToday = upperSeries[lastIndex]
  const lowerToday = lowerSeries[lastIndex]
  const insideRatio = calcInsideRatio(list, upperSeries, lowerSeries, startIndex)
  const upperSlope = calcSlope(upperSeries)
  const lowerSlope = calcSlope(lowerSeries)
  const upperDown = upperSlope !== null && upperSlope < -0.005
  const lowerDown = lowerSlope !== null && lowerSlope < -0.003

  let status = 'none'
  let breakout = false
  let daysSinceBreakout = null
  if (close !== null && upperToday !== null && lowerToday !== null) {
    breakout = close > upperToday * 1.01
    if (breakout) {
      status = 'breakout'
      daysSinceBreakout = 0
      for (let i = lastIndex - 1; i >= startIndex; i--) {
        const dayClose = toNum(list[i].close)
        const dayUpper = upperSeries[i]
        if (dayClose === null || dayUpper === null || dayClose <= dayUpper * 1.01) break
        daysSinceBreakout += 1
      }
    } else if (close < lowerToday * 0.99) {
      status = 'below'
    } else if (upperDown && lowerDown && insideRatio >= 0.45) {
      status = 'inside'
    }
  }

  const slopeScore = Number(upperDown) + Number(lowerDown)
  const confidence = Math.min(0.98, (slopeScore / 2) * 0.45 + insideRatio * 0.55)

  return {
    status,
    breakout,
    daysSinceBreakout,
    confidence,
    upperToday,
    lowerToday,
    upperSlope,
    lowerSlope,
    insideRatio,
    upperSeries,
    lowerSeries
  }
}

async function loadData () {
  if (!symbol.value) return
  loading.value = true
  dataReady.value = false
  try {
    const data = await stockDailyApi.getBySymbol(symbol.value)
    if (!Array.isArray(data) || data.length === 0) {
      channelInfo.value = null
      return
    }

    const list = data
      .slice()
      .sort((a, b) => a.trade_date.localeCompare(b.trade_date))
    latestQuote.value = list[list.length - 1]

    const dates = list.map(item => item.trade_date)
    const klineData = list.map(item => [item.open, item.close, item.low, item.high])
    const volumes = list.map(item => ({
      value: item.volume,
      itemStyle: { color: item.close >= item.open ? '#ef4444' : '#22c55e' }
    }))
    const channel = calcChannel(list)
    channelInfo.value = channel
    initChart(dates, klineData, volumes, list, channel)
    dataReady.value = true
  } catch (error) {
    console.error('加载动态边界数据失败:', error)
    channelInfo.value = null
  } finally {
    loading.value = false
  }
}

function initChart (dates, data, volumes, list, channel) {
  if (!chartRef.value) return
  if (chartRef.value.clientHeight === 0) {
    requestAnimationFrame(() => initChart(dates, data, volumes, list, channel))
    return
  }

  const total = dates.length
  const defaultCount = Math.min(lookbackDays.value || 80, 120)
  const startPercent = total > defaultCount ? ((total - defaultCount) / total) * 100 : 0
  const isDark = document.documentElement.dataset.theme !== 'light'
  const axisColor = isDark ? '#52525b' : '#adb5bd'
  const splitLineColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'
  const textColor = isDark ? '#a1a1aa' : '#495057'
  const tooltipBg = isDark ? 'rgba(17, 17, 17, 0.95)' : 'rgba(255,255,255,0.98)'
  const tooltipBorder = isDark ? '#2a2a2a' : '#dee2e6'
  const tooltipText = isDark ? '#f5f5f5' : '#212529'

  init({
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      backgroundColor: tooltipBg,
      borderColor: tooltipBorder,
      textStyle: {
        color: tooltipText,
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: 11
      },
      formatter (params) {
        const kline = params.find((p) => p.seriesName === '日K')
        if (!kline) return ''
        const item = list[kline.dataIndex]
        if (!item) return ''
        const upper = params.find((p) => p.seriesName === '上边界')
        const lower = params.find((p) => p.seriesName === '下边界')
        const changeColor = item.pct_chg >= 0 ? '#ef4444' : '#22c55e'

        let html = '<div style="font-family:Consolas,Menlo,monospace;font-size:11px;line-height:1.7;">'
        html += `<div style="font-weight:600;margin-bottom:6px;letter-spacing:0.06em;">${kline.axisValue}</div>`
        html += `<div>OPEN&nbsp;&nbsp;${formatNumber(item.open)}</div>`
        html += `<div>CLOSE&nbsp;${formatNumber(item.close)}</div>`
        html += `<div>HIGH&nbsp;&nbsp;${formatNumber(item.high)}</div>`
        html += `<div>LOW&nbsp;&nbsp;&nbsp;${formatNumber(item.low)}</div>`
        html += `<div>CHG&nbsp;&nbsp;&nbsp;<span style="color:${changeColor};font-weight:600">${formatPercent(item.pct_chg)}</span></div>`
        if (upper?.data != null) html += `<div>UPPER&nbsp;${upper.data}</div>`
        if (lower?.data != null) html += `<div>LOWER&nbsp;${lower.data}</div>`
        html += '</div>'
        return html
      }
    },
    legend: {
      data: ['日K', '上边界', '下边界'],
      right: 16,
      top: 8,
      textStyle: {
        color: textColor,
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: 10
      },
      icon: 'roundRect',
      itemWidth: 16,
      itemHeight: 3
    },
    grid: [
      { left: '8%', right: '4%', top: '6%', height: isMobile.value ? '58%' : '60%' },
      { left: '8%', right: '4%', top: isMobile.value ? '72%' : '74%', height: isMobile.value ? '16%' : '14%' }
    ],
    xAxis: [
      {
        type: 'category',
        data: dates,
        scale: true,
        boundaryGap: false,
        axisLine: { onZero: false, lineStyle: { color: axisColor } },
        axisLabel: { color: textColor, fontFamily: 'Consolas, Menlo, monospace', fontSize: 10 },
        splitLine: { show: false },
        min: 'dataMin',
        max: 'dataMax'
      },
      {
        type: 'category',
        gridIndex: 1,
        data: dates,
        scale: true,
        boundaryGap: true,
        axisLine: { onZero: false, lineStyle: { color: axisColor } },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
        min: 'dataMin',
        max: 'dataMax'
      }
    ],
    yAxis: [
      {
        scale: true,
        position: 'right',
        axisLine: { lineStyle: { color: axisColor } },
        axisLabel: { color: textColor, fontFamily: 'Consolas, Menlo, monospace', fontSize: 10 },
        splitLine: { lineStyle: { color: splitLineColor } }
      },
      {
        scale: true,
        gridIndex: 1,
        splitNumber: 2,
        position: 'right',
        axisLabel: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false }
      }
    ],
    dataZoom: [
      { type: 'inside', xAxisIndex: [0, 1], start: startPercent, end: 100 },
      {
        show: true,
        xAxisIndex: [0, 1],
        type: 'slider',
        top: isMobile.value ? '90%' : '92%',
        start: startPercent,
        end: 100,
        height: isMobile.value ? 28 : 18,
        borderColor: 'transparent',
        fillerColor: 'rgba(59, 130, 246, 0.12)',
        handleStyle: { color: '#3b82f6' },
        textStyle: { color: textColor, fontFamily: 'Consolas, Menlo, monospace', fontSize: 9 },
        dataBackground: {
          lineStyle: { color: axisColor, opacity: 0.3 },
          areaStyle: { color: axisColor, opacity: 0.15 }
        },
        selectedDataBackground: {
          lineStyle: { color: '#3b82f6' },
          areaStyle: { color: 'rgba(59, 130, 246, 0.2)' }
        }
      }
    ],
    series: [
      {
        name: '日K',
        type: 'candlestick',
        data,
        itemStyle: {
          color: '#ef4444',
          color0: '#22c55e',
          borderColor: '#ef4444',
          borderColor0: '#22c55e'
        }
      },
      {
        name: '上边界',
        type: 'line',
        data: channel.upperSeries,
        connectNulls: false,
        smooth: false,
        showSymbol: false,
        lineStyle: { width: 2.4, color: '#ef4444' }
      },
      {
        name: '下边界',
        type: 'line',
        data: channel.lowerSeries,
        connectNulls: false,
        smooth: false,
        showSymbol: false,
        lineStyle: { width: 2.4, color: '#2563eb' }
      },
      {
        name: '成交量',
        type: 'bar',
        gridIndex: 1,
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: volumes
      }
    ]
  })
  resize()
}

function applySettings () {
  loadData()
}

onMounted(() => {
  loadData()
})
</script>

<template>
  <div class="trend-channel-page page">
    <div class="detail-bar">
      <button class="back-btn" @click="goBack">
        <span class="arrow">←</span>
        <span>返回</span>
      </button>
      <span class="bar-divider" />
      <div class="bar-meta">
        <span class="bar-section">动态边界</span>
        <h1 class="bar-name">{{ stockName || '—' }}</h1>
        <span class="bar-symbol">{{ symbol }}</span>
        <span v-if="latestQuote" class="bar-price">
          <span class="price-num">{{ formatNumber(latestQuote.close) }}</span>
          <span
            class="price-chg"
            :class="latestQuote.pct_chg >= 0 ? 'up' : 'down'"
          >{{ formatPercent(latestQuote.pct_chg) }}</span>
        </span>
      </div>
    </div>

    <div class="tool-row">
      <label class="field">
        <span>回看</span>
        <input v-model.number="lookbackDays" type="number" min="30" max="160" @keyup.enter="applySettings">
      </label>
      <label class="field">
        <span>窗口</span>
        <input v-model.number="boundaryWindow" type="number" min="5" max="31" @keyup.enter="applySettings">
      </label>
      <button class="btn-primary" @click="applySettings">重算</button>
      <span class="status-pill" :class="statusClass">{{ statusText }}</span>
    </div>

    <div class="chart-frame">
      <div ref="chartRef" class="kline-chart"></div>
      <div v-if="loading" class="kline-loading">载入边界…</div>
      <div v-else-if="!dataReady" class="kline-empty">暂无数据</div>
    </div>

    <div class="metrics-panel">
      <div class="metric-item">
        <span class="metric-label">上边界</span>
        <span class="metric-value">{{ formatNumber(channelInfo?.upperToday) }}</span>
      </div>
      <div class="metric-item">
        <span class="metric-label">下边界</span>
        <span class="metric-value">{{ formatNumber(channelInfo?.lowerToday) }}</span>
      </div>
      <div class="metric-item">
        <span class="metric-label">上边界斜率</span>
        <span class="metric-value">{{ formatNumber(channelInfo?.upperSlope, 4) }}</span>
      </div>
      <div class="metric-item">
        <span class="metric-label">下边界斜率</span>
        <span class="metric-value">{{ formatNumber(channelInfo?.lowerSlope, 4) }}</span>
      </div>
      <div class="metric-item">
        <span class="metric-label">通道占比</span>
        <span class="metric-value">{{ formatPercent((channelInfo?.insideRatio || 0) * 100) }}</span>
      </div>
      <div class="metric-item">
        <span class="metric-label">置信度</span>
        <span class="metric-value">{{ formatPercent((channelInfo?.confidence || 0) * 100) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.trend-channel-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 4px 28px 18px;
  box-sizing: border-box;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 4px 16px 12px;
  }
}

.detail-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 0 14px;
  border-bottom: 1px solid var(--rule);
  margin-bottom: 12px;
  flex-shrink: 0;
}

.back-btn,
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 4px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  transition: all 0.2s;
}

.back-btn {
  background: transparent;
  border: 1px solid var(--rule);
  padding: 7px 12px;
  color: var(--text-muted);

  .arrow { font-size: 13px; }

  &:hover {
    color: var(--text-primary);
    border-color: var(--text-faint);
  }
}

.btn-primary {
  border: none;
  padding: 7px 14px;
  color: white;
  background: var(--accent);

  &:hover { background: var(--accent-hover); }
}

.bar-divider {
  width: 1px;
  height: 22px;
  background: var(--rule);
}

.bar-meta {
  display: flex;
  align-items: baseline;
  gap: 14px;
  flex-wrap: wrap;
}

.bar-section {
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.22em;
  color: var(--text-faint);
}

.bar-name {
  margin: 0;
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: 0;
  line-height: 1.2;
}

.bar-symbol {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
}

.bar-price {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  margin-left: 6px;
  font-variant-numeric: tabular-nums;

  .price-num {
    font-family: var(--font-mono);
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .price-chg {
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 2px;
    letter-spacing: 0.04em;

    &.up {
      color: var(--up);
      background: rgba(239, 68, 68, 0.12);
    }

    &.down {
      color: var(--down);
      background: rgba(34, 197, 94, 0.12);
    }
  }
}

.tool-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);

  input {
    width: 72px;
    padding: 6px 8px;
    box-sizing: border-box;
    border: 1px solid var(--rule);
    border-radius: 4px;
    background: var(--bg-input);
    color: var(--text-primary);
    font-family: var(--font-mono);
    font-size: 12px;
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid var(--rule);
  border-radius: 4px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 11px;

  &.up {
    color: var(--up);
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.18);
  }

  &.down {
    color: var(--down);
    background: rgba(34, 197, 94, 0.1);
    border-color: rgba(34, 197, 94, 0.18);
  }
}

.chart-frame {
  flex: 1;
  min-height: 430px;
  position: relative;
  border: 1px solid var(--rule);
  border-radius: 4px;
  background: var(--bg-secondary);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: 4px;
    box-shadow: var(--shadow-soft);
    z-index: 1;
  }
}

.kline-chart {
  width: 100%;
  height: 100%;
}

.kline-loading,
.kline-empty {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-faint);
}

.metrics-panel {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.metric-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--rule);
  border-radius: 4px;
  background: var(--bg-secondary);
}

.metric-label {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-faint);
}

.metric-value {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
